import { useState, useEffect, useRef } from "react";

/**
 * Проверка заполнены ли все обязательные поля
 * @param {Object} formData - данные формы
 * @param {Object} requiredFields - объект с полями для проверки
 * @returns {Object} - объект с информацией о незаполненных полях
 */
export const checkRequiredFields = (formData, requiredFields) => {
  const emptyFields = [];
  const fieldErrors = {};

  for (const [field, label] of Object.entries(requiredFields)) {
    const isEmpty = !formData[field] || formData[field].trim() === "";
    fieldErrors[field] = isEmpty;

    if (isEmpty) {
      emptyFields.push(label);
    }
  }

  return {
    isEmpty: emptyFields.length > 0,
    emptyFields,
    fieldErrors,
    message: emptyFields.length > 0 ? "Не заполнены обязательные поля" : null,
  };
};

/**
 * Общий хук для валидации формы
 * @param {Object} initialFormData - начальные данные формы
 * @param {Object} validationConfig - конфигурация валидации
 * @returns {Object} - объект с состоянием и функциями валидации
 */
export const useFormValidation = (initialFormData, validationConfig) => {
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [requiredFieldsError, setRequiredFieldsError] = useState(null);
  const [touched, setTouched] = useState({});

  const isInitialized = useRef(false);

  useEffect(() => {
    if (!isInitialized.current) {
      const initialErrors = {};
      const initialMessages = {};
      const initialTouched = {};

      Object.keys(initialFormData).forEach((field) => {
        initialErrors[field] = false;
        initialMessages[field] = "";
        initialTouched[field] = false;
      });

      setFieldErrors(initialErrors);
      setErrorMessages(initialMessages);
      setTouched(initialTouched);
      isInitialized.current = true;
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: false }));

    if (validationConfig.clearOnChange?.includes(field)) {
      setErrorMessages((prev) => ({ ...prev, [field]: "" }));
    }

    setRequiredFieldsError(null);
  };

  const handleBlur = (field, value) => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    if (validationConfig.validateOnBlur?.[field]) {
      const error = validationConfig.validateOnBlur[field](value, formData);
      setErrorMessages((prev) => ({ ...prev, [field]: error || "" }));
      setFieldErrors((prev) => ({ ...prev, [field]: !!error }));
    }
  };

  const validateForm = () => {
    const result = validationConfig.validate(formData);

    setFieldErrors(result.fieldErrors);
    setErrorMessages(result.messages || {});
    setRequiredFieldsError(result.requiredFieldsError);

    const allTouched = {};
    Object.keys(formData).forEach((field) => {
      allTouched[field] = true;
    });
    setTouched(allTouched);

    return {
      isValid: result.isValid,
      requiredFieldsError: result.requiredFieldsError,
    };
  };

  const resetForm = () => {
    setFormData(initialFormData);
    const resetErrors = {};
    const resetMessages = {};
    Object.keys(initialFormData).forEach((field) => {
      resetErrors[field] = false;
      resetMessages[field] = "";
    });
    setFieldErrors(resetErrors);
    setErrorMessages(resetMessages);
    setRequiredFieldsError(null);

    const resetTouched = {};
    Object.keys(initialFormData).forEach((field) => {
      resetTouched[field] = false;
    });
    setTouched(resetTouched);
  };

  return {
    formData,
    fieldErrors,
    errorMessages,
    requiredFieldsError,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    resetForm,
    setFormData,
  };
};
